import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import {
  Badge,
  Box,
  Button,
  Center,
  Code,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Avatar } from '../components/Avatar';
import {
  ArrowLeft,
  Copy,
  Crown,
  Download,
  Pencil,
  QrCode,
  RefreshCw,
  Shield,
  Trash2,
  Share2,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { useAuth, useFamily, useFamilyAdmin, useLoadingState } from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { TwindenQr } from '../components/brand/TwindenQr';
import { auditActionLabel } from '../lib/audit';
import { AvatarUpload } from '../components/family/AvatarUpload';
import { pictureErrorMessage } from '../lib/pictureSetting';
import { AddSomeoneYouKnow } from '../components/family/AddSomeoneYouKnow';
import { InvitePeopleSheet } from '../components/family/InvitePeopleSheet';
import { FamilyReportsPanel } from '../components/family/FamilyReportsPanel';
import { JoinRequestsPanel } from '../components/family/JoinRequestsPanel';
import { joinRequestCountsAtom } from '../store';
import {
  PageScaffold,
  Section,
  SettingRow,
} from '../components/page/PageScaffold';
import { DeleteFamilySheet } from '../components/family/DeleteFamilySheet';
import { FamilyNameHandleDialog } from '../components/family/FamilyNameHandleDialog';
import { FamilyExportSheet } from '../components/family/FamilyExportSheet';
import {
  ApiError,
  type CreatedEnrollmentToken,
  type FamilyRole,
  type EnrollmentTokenRow,
} from '../lib/api';
import { appLocale } from '../lib/format';
import { t as translateNow, useT, type MessageKey } from '../i18n';

const ROLE_LABEL: Record<FamilyRole, MessageKey> = {
  root: 'account.role.root',
  admin: 'account.role.admin',
  member: 'account.role.member',
};

const ROLE_COLOR: Record<FamilyRole, string> = {
  root: 'brand',
  admin: 'orange',
  member: 'gray',
};

function timeAgo(input: string) {
  const then = new Date(input).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return translateNow('account.admin.justNow');
  const minutes = Math.round(seconds / 60);
  if (minutes < 60)
    return translateNow('account.admin.minutesAgo', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24)
    return translateNow('account.admin.hoursAgo', { count: hours });
  const days = Math.round(hours / 24);
  return translateNow('account.admin.daysAgo', { count: days });
}

export function FamilyAdminPage() {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { currentUser } = useAuth();
  const { updateFamily, loadFamilies } = useFamily();

  const {
    family,
    tokens,
    invites,
    audit,
    loading,
    error,
    failure,
    refresh,
    sendInvite,
    revokeToken,
    regenerateToken,
    setMemberRole,
    removeMember,
  } = useFamilyAdmin(familyId);
  // The mark while the family loads (M66, D248).
  const familyState = useLoadingState({
    loading,
    hasData: Boolean(family),
    error: failure,
    retry: () => void refresh(),
  });

  // Name and handle (M59, D216).
  const nameHandleDialog = useDisclosure();

  // A personal link asks only for the name and role (D348): the person
  // picks their own handle when they open it, if they're new.
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<FamilyRole>('member');
  const familyLink = useDisclosure();
  // Danger zone (M54): download (roots and admins, D192), delete (roots, D187).
  const exportSheet = useDisclosure();
  const deleteSheet = useDisclosure();

  // ?tab=requests opens the Requests tab (the approvers' push links there).
  const [searchParams, setSearchParams] = useSearchParams();
  const [requestCounts, setRequestCounts] = useAtom(joinRequestCountsAtom);
  const setRequestCount = useCallback(
    (count: number) => {
      if (!familyId) return;
      setRequestCounts((current) =>
        current[familyId] === count
          ? current
          : { ...current, [familyId]: count }
      );
    },
    [familyId, setRequestCounts]
  );

  const [created, setCreated] = useState<CreatedEnrollmentToken | null>(null);
  const qrDialog = useDisclosure();

  const myRole = family?.currentUserRole ?? 'member';
  const isRoot = myRole === 'root';
  const canApprove = myRole === 'root' || myRole === 'admin';
  const tabNames = [
    'members',
    'invite',
    ...(canApprove ? ['requests'] : []),
    // Reports are for roots (M58, D207).
    ...(isRoot ? ['reports'] : []),
    'pending',
    'activity',
  ];
  const tabIndex = Math.max(0, tabNames.indexOf(searchParams.get('tab') ?? ''));
  const setTab = (index: number) =>
    setSearchParams(
      (params) => {
        params.set('tab', tabNames[index] ?? 'members');
        return params;
      },
      { replace: true }
    );
  const requestCount = familyId ? (requestCounts[familyId] ?? 0) : 0;
  // On phones the tabs scroll sideways: keep the open one in sight, e.g.
  // when a link opens ?tab=activity.
  const tabListRef = useRef<HTMLDivElement>(null);
  const familyShown = !!family;
  // Badges arriving later widen the tabs, so look again then too.
  const tabBadges = `${requestCount}/${tokens.length}/${invites.length}`;
  useEffect(() => {
    tabListRef.current
      ?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [tabIndex, familyShown, tabBadges]);

  const notifyError = (err: unknown, title: string) =>
    toast({
      title,
      description:
        err instanceof ApiError
          ? err.message
          : t('account.profile.unknownError'),
      status: 'error',
      duration: 6000,
    });

  const showCreated = (result: CreatedEnrollmentToken) => {
    setCreated(result);
    qrDialog.onOpen();
  };

  // Family icon (D9): roots/admins set it, sealed with the family key on
  // this device first (D291).
  const handleFamilyAvatar = async (picture: string | null) => {
    if (!family) return;
    try {
      await updateFamily(family.id, { picture });
      await refresh();
      toast({
        title: picture
          ? t('account.admin.iconUpdated')
          : t('account.admin.iconRemoved'),
        status: 'success',
      });
    } catch (err) {
      toast({
        title: t('account.admin.iconFailed'),
        description: pictureErrorMessage(err),
        status: 'error',
        duration: 6000,
      });
    }
  };

  const handleInviteNew = async () => {
    try {
      const result = await sendInvite({
        kind: 'enroll',
        displayName: newName.trim(),
        familyRole: newRole,
      });
      setNewName('');
      setNewRole('member');
      showCreated(result);
    } catch (err) {
      notifyError(err, t('account.admin.createInviteFailed'));
    }
  };

  const handleRoleChange = async (userId: string, role: FamilyRole) => {
    try {
      await setMemberRole(userId, role);
      toast({
        title: t('account.admin.roleChanged', { role: t(ROLE_LABEL[role]) }),
        status: 'success',
      });
    } catch (err) {
      notifyError(err, t('account.admin.roleFailed'));
    }
  };

  const handleRemove = async (userId: string, name: string) => {
    if (
      !window.confirm(
        t('account.admin.removeConfirm', { name, family: family?.name ?? '' })
      )
    )
      return;
    try {
      await removeMember(userId);
      toast({ title: t('account.admin.removed', { name }), status: 'success' });
    } catch (err) {
      notifyError(err, t('account.admin.removeFailed'));
    }
  };

  const copyLink = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: t('account.profile.linkCopied'), status: 'success' });
    } catch {
      toast({
        title: t('account.admin.copyFailed'),
        status: 'warning',
      });
    }
  };

  if (familyState.show) {
    return (
      <Center h="60vh">
        <LoadingPanel state={familyState} />
      </Center>
    );
  }

  if (!family) {
    return (
      <Center h="60vh">
        <VStack>
          <Text color="muted">{error ?? t('account.admin.notFound')}</Text>
          <Button
            leftIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/family')}
          >
            {t('account.admin.backToFamilies')}
          </Button>
        </VStack>
      </Center>
    );
  }

  const pendingTokens = tokens.filter((token) => token.status === 'pending');
  const pendingInvites = invites.filter((i) => i.status === 'pending');

  // Personal links for new people only: a device is added by its owner,
  // never from here (D334, #105).
  // Links from before D348 still show the handle they hold.
  const tokenSubtitle = (token: EnrollmentTokenRow) =>
    token.handle
      ? t('account.admin.tokenEnroll', {
          handle: token.handle,
          name: token.displayName ?? '',
          role: t(ROLE_LABEL[token.familyRole]),
        })
      : t('account.admin.tokenEnrollName', {
          name: token.displayName ?? '',
          role: t(ROLE_LABEL[token.familyRole]),
        });

  return (
    <PageScaffold
      backTo="/family"
      maxW="900px"
      title={
        <HStack spacing={3} align="center">
          <AvatarUpload
            value={family.avatarUrl}
            onChange={handleFamilyAvatar}
            size="md"
            name={family.name}
            ownerId={family.id}
            familyId={family.id}
          />
          <Box minW={0}>
            <Heading as="h1" fontSize="30px" lineHeight={1.05} noOfLines={2}>
              {family.name}
            </Heading>
            {/* Tells same-named families apart (D217). */}
            {family.handle && (
              <Text color="muted" fontSize="15px" mt={1} noOfLines={1}>
                @{family.handle}
              </Text>
            )}
          </Box>
          <IconButton
            aria-label={t('account.familyHandle.edit')}
            icon={<Pencil size={16} />}
            variant="ghost"
            colorScheme="gray"
            onClick={nameHandleDialog.onOpen}
          />
        </HStack>
      }
      subtitle={t(
        family.members.length === 1
          ? 'account.admin.subtitleOne'
          : 'account.admin.subtitleOther',
        {
          role: t(ROLE_LABEL[myRole]).toLowerCase(),
          count: family.members.length,
        }
      )}
    >
      <Stack spacing={6}>
        {error && (
          <Text color="red.500" fontSize="sm">
            {error}
          </Text>
        )}

        <Tabs
          variant="soft-rounded"
          colorScheme="brand"
          index={tabIndex}
          onChange={setTab}
          isLazy
        >
          {/* Tabs keep their width and scroll, rather than squeeze. */}
          <TabList
            ref={tabListRef}
            overflowX="auto"
            pb={1}
            sx={{ '& > [role="tab"]': { flexShrink: 0 } }}
          >
            <Tab>{t('account.admin.members')}</Tab>
            <Tab>{t('account.admin.invite')}</Tab>
            {canApprove && (
              <Tab>
                {t('account.requests.tab')}
                {requestCount > 0 && (
                  <Badge
                    ml={2}
                    bg="saffron"
                    color="#1F1D1B"
                    borderRadius="full"
                  >
                    {requestCount}
                  </Badge>
                )}
              </Tab>
            )}
            {isRoot && <Tab>{t('account.reports.tab')}</Tab>}
            <Tab>
              {t('account.admin.pending')}
              {pendingTokens.length + pendingInvites.length > 0
                ? ` (${pendingTokens.length + pendingInvites.length})`
                : ''}
            </Tab>
            <Tab>{t('account.admin.activity')}</Tab>
          </TabList>

          <TabPanels>
            {/* ---------------- Members ---------------- */}
            <TabPanel px={0}>
              <VStack align="stretch" spacing={3}>
                {family.members.map((member) => {
                  const isSelf = member.userId === currentUser?.id;
                  return (
                    <Box
                      key={member.id}
                      bg="surface"
                      p={4}
                      borderRadius="card"
                      borderWidth="1px"
                      borderColor="line"
                    >
                      <HStack justify="space-between">
                        <HStack spacing={3}>
                          <Avatar
                            name={member.user.displayName}
                            src={member.user.avatarUrl ?? undefined}
                            ownerId={member.user.id}
                            familyId={family.id}
                            size="sm"
                          />
                          <VStack align="start" spacing={0}>
                            <HStack>
                              <Text fontWeight="medium">
                                {member.user.displayName}
                              </Text>
                              <Badge colorScheme={ROLE_COLOR[member.role]}>
                                {t(ROLE_LABEL[member.role])}
                              </Badge>
                              {isSelf && (
                                <Badge variant="outline" colorScheme="green">
                                  {t('account.admin.youBadge')}
                                </Badge>
                              )}
                            </HStack>
                            <Text fontSize="sm" color="muted">
                              @{member.user.handle}
                              {member.nickname
                                ? ` · “${member.nickname}”`
                                : ''}{' '}
                              ·{' '}
                              {t('account.admin.joined', {
                                time: timeAgo(member.joinedAt),
                              })}
                            </Text>
                          </VStack>
                        </HStack>

                        {!isSelf && (
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              aria-label={t('account.admin.memberActions')}
                              icon={<Shield size={16} />}
                              variant="ghost"
                              size="sm"
                              isDisabled={!isRoot && myRole !== 'admin'}
                            />
                            <MenuList>
                              {isRoot && member.role !== 'root' && (
                                <MenuItem
                                  icon={<Crown size={16} />}
                                  onClick={() =>
                                    handleRoleChange(member.userId, 'root')
                                  }
                                >
                                  {t('account.admin.makeRoot')}
                                </MenuItem>
                              )}
                              {isRoot &&
                                member.role !== 'admin' &&
                                member.role !== 'root' && (
                                  <MenuItem
                                    icon={<Shield size={16} />}
                                    onClick={() =>
                                      handleRoleChange(member.userId, 'admin')
                                    }
                                  >
                                    {t('account.admin.makeAdmin')}
                                  </MenuItem>
                                )}
                              {isRoot && member.role !== 'member' && (
                                <MenuItem
                                  icon={<UserPlus size={16} />}
                                  onClick={() =>
                                    handleRoleChange(member.userId, 'member')
                                  }
                                >
                                  {t('account.admin.makeMember')}
                                </MenuItem>
                              )}
                              <MenuItem
                                icon={<Trash2 size={16} />}
                                color="red.500"
                                onClick={() =>
                                  handleRemove(
                                    member.userId,
                                    member.user.displayName
                                  )
                                }
                              >
                                {t('account.admin.removeFromFamily')}
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        )}
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            </TabPanel>

            {/* ---------------- Invite ---------------- */}
            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <Box
                  bg="surface"
                  p={5}
                  borderRadius="card"
                  borderWidth="1px"
                  borderColor="line"
                >
                  <VStack align="stretch" spacing={4}>
                    <VStack align="start" spacing={0}>
                      <Heading size="sm">
                        {t('account.admin.inviteNew')}
                      </Heading>
                      <Text fontSize="sm" color="muted">
                        {t('account.admin.inviteNewHint')}
                      </Text>
                    </VStack>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">
                        {t('account.admin.displayName')}
                      </FormLabel>
                      <Input
                        placeholder="Priya Sharma"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        {t('account.admin.role')}
                      </FormLabel>
                      <Select
                        value={newRole}
                        onChange={(e) =>
                          setNewRole(e.target.value as FamilyRole)
                        }
                      >
                        <option value="member">
                          {t('account.role.member')}
                        </option>
                        {isRoot && (
                          <option value="admin">
                            {t('account.role.admin')}
                          </option>
                        )}
                        {isRoot && (
                          <option value="root">{t('account.role.root')}</option>
                        )}
                      </Select>
                    </FormControl>
                    <Button
                      colorScheme="brand"
                      leftIcon={<QrCode size={16} />}
                      isDisabled={!newName.trim()}
                      onClick={handleInviteNew}
                    >
                      {t('account.admin.createInvite')}
                    </Button>
                  </VStack>
                </Box>

                {canApprove && (
                  <AddSomeoneYouKnow
                    familyId={family.id}
                    canGrantRoot={isRoot}
                    onInvited={() => void refresh()}
                  />
                )}

                <Box
                  bg="surface"
                  p={5}
                  borderRadius="card"
                  borderWidth="1px"
                  borderColor="line"
                  alignSelf="start"
                >
                  <VStack align="stretch" spacing={4}>
                    <VStack align="start" spacing={0}>
                      <Heading size="sm">
                        {t('account.invite.title', { family: family.name })}
                      </Heading>
                      <Text fontSize="sm" color="muted">
                        {t('account.invite.cardBody')}
                      </Text>
                    </VStack>
                    <Button
                      variant="outline"
                      colorScheme="gray"
                      borderColor="line"
                      leftIcon={<Share2 size={16} />}
                      onClick={familyLink.onOpen}
                    >
                      {t('account.invite.open')}
                    </Button>
                  </VStack>
                </Box>
              </SimpleGrid>
            </TabPanel>

            {/* ---------------- Requests ---------------- */}
            {canApprove && (
              <TabPanel px={0}>
                <VStack align="stretch" spacing={3}>
                  <Heading size="sm">{t('account.requests.title')}</Heading>
                  <JoinRequestsPanel
                    familyId={family.id}
                    canGrantRoot={isRoot}
                    onCountChange={setRequestCount}
                  />
                </VStack>
              </TabPanel>
            )}

            {/* ---------------- Reports (M58) ---------------- */}
            {isRoot && (
              <TabPanel px={0}>
                <VStack align="stretch" spacing={3}>
                  <Heading size="sm">{t('account.reports.title')}</Heading>
                  <FamilyReportsPanel familyId={family.id} />
                </VStack>
              </TabPanel>
            )}

            {/* ---------------- Pending ---------------- */}
            <TabPanel px={0}>
              <VStack align="stretch" spacing={6}>
                <VStack align="stretch" spacing={3}>
                  <Heading size="sm">{t('account.admin.pendingLinks')}</Heading>
                  {pendingTokens.length === 0 ? (
                    <Text fontSize="sm" color="muted">
                      {t('account.admin.noPendingLinks')}
                    </Text>
                  ) : (
                    pendingTokens.map((token) => (
                      <Box
                        key={token.id}
                        bg="surface"
                        p={4}
                        borderRadius="card"
                        borderWidth="1px"
                        borderColor="line"
                      >
                        <HStack justify="space-between">
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium">
                              {tokenSubtitle(token)}
                            </Text>
                            <Text fontSize="xs" color="muted">
                              {t('account.admin.createdExpires', {
                                ago: timeAgo(token.createdAt),
                                date: new Date(
                                  token.expiresAt
                                ).toLocaleDateString(appLocale()),
                              })}
                            </Text>
                          </VStack>
                          <HStack>
                            <Button
                              size="sm"
                              variant="ghost"
                              leftIcon={<RefreshCw size={14} />}
                              onClick={async () => {
                                try {
                                  showCreated(await regenerateToken(token.id));
                                } catch (err) {
                                  notifyError(
                                    err,
                                    t('account.admin.regenerateFailed')
                                  );
                                }
                              }}
                            >
                              {t('account.admin.newLink')}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              leftIcon={<XCircle size={14} />}
                              onClick={async () => {
                                try {
                                  await revokeToken(token.id);
                                } catch (err) {
                                  notifyError(
                                    err,
                                    t('account.admin.revokeFailed')
                                  );
                                }
                              }}
                            >
                              {t('account.admin.revoke')}
                            </Button>
                          </HStack>
                        </HStack>
                      </Box>
                    ))
                  )}
                </VStack>

                <Divider />

                <VStack align="stretch" spacing={3}>
                  <Heading size="sm">
                    {t('account.admin.pendingInvites')}
                  </Heading>
                  {pendingInvites.length === 0 ? (
                    <Text fontSize="sm" color="muted">
                      {t('account.admin.noPendingInvites')}
                    </Text>
                  ) : (
                    pendingInvites.map((invite) => (
                      <Box
                        key={invite.id}
                        bg="surface"
                        p={4}
                        borderRadius="card"
                        borderWidth="1px"
                        borderColor="line"
                      >
                        <HStack justify="space-between">
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium">
                              {t('account.admin.handleAs', {
                                handle:
                                  invite.targetUser?.handle ??
                                  invite.targetUserId,
                                role: t(ROLE_LABEL[invite.familyRole]),
                              })}
                              {invite.targetUser?.displayName
                                ? ` · ${invite.targetUser.displayName}`
                                : ''}
                            </Text>
                            <Text fontSize="xs" color="muted">
                              {t('account.admin.invitedExpires', {
                                ago: timeAgo(invite.createdAt),
                                date: new Date(
                                  invite.expiresAt
                                ).toLocaleDateString(appLocale()),
                              })}
                            </Text>
                          </VStack>
                          <Badge colorScheme="yellow">
                            {t('account.admin.waiting')}
                          </Badge>
                        </HStack>
                      </Box>
                    ))
                  )}
                </VStack>
              </VStack>
            </TabPanel>

            {/* ---------------- Activity ---------------- */}
            <TabPanel px={0}>
              <VStack align="stretch" spacing={2}>
                {audit.length === 0 ? (
                  <Text fontSize="sm" color="muted">
                    {t('account.admin.noActivity')}
                  </Text>
                ) : (
                  audit.map((entry) => (
                    <Box
                      key={entry.id}
                      bg="surface.subtle"
                      p={3}
                      borderRadius="md"
                    >
                      <HStack justify="space-between" align="start">
                        <VStack align="start" spacing={0}>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            title={entry.action}
                          >
                            {auditActionLabel(entry.action)}
                          </Text>
                          <Text fontSize="xs" color="muted">
                            {entry.actorIsSystemRoot
                              ? t('account.admin.systemRoot')
                              : entry.actor?.handle
                                ? `@${entry.actor.handle}`
                                : // A deleted account has no handle (D173).
                                  (entry.actor?.displayName ??
                                  entry.actorUserId)}
                            {entry.metadata
                              ? ` · ${JSON.stringify(entry.metadata).slice(0, 120)}`
                              : ''}
                          </Text>
                        </VStack>
                        <Text fontSize="xs" color="muted">
                          {timeAgo(entry.createdAt)}
                        </Text>
                      </HStack>
                    </Box>
                  ))
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {canApprove && (
          <Section
            title={
              <Text as="span" color="danger">
                {t('account.dangerZone.title')}
              </Text>
            }
            description={t(
              isRoot
                ? 'account.dangerZone.body'
                : 'account.dangerZone.bodyAdmin'
            )}
          >
            <SettingRow
              label={t('account.exportFamily.row')}
              hint={t('account.exportFamily.rowHint')}
            >
              <Button
                h="40px"
                variant="outline"
                colorScheme="gray"
                borderColor="line"
                leftIcon={<Download size={16} />}
                onClick={exportSheet.onOpen}
              >
                {t('account.exportFamily.button')}
              </Button>
            </SettingRow>
            {isRoot && (
              <SettingRow
                label={t('account.deleteFamily.row')}
                hint={t('account.deleteFamily.rowHint')}
              >
                <Button
                  h="40px"
                  variant="outline"
                  colorScheme="red"
                  leftIcon={<Trash2 size={16} />}
                  onClick={deleteSheet.onOpen}
                >
                  {t('account.deleteFamily.button')}
                </Button>
              </SettingRow>
            )}
          </Section>
        )}
      </Stack>

      {canApprove && (
        <FamilyExportSheet
          isOpen={exportSheet.isOpen}
          onClose={exportSheet.onClose}
          familyId={family.id}
          familyName={family.name}
          currentUserId={currentUser?.id}
        />
      )}
      {isRoot && (
        <DeleteFamilySheet
          isOpen={deleteSheet.isOpen}
          onClose={deleteSheet.onClose}
          familyId={family.id}
          familyName={family.name}
          familyHandle={family.handle}
          onDownloadFirst={() => {
            // Download first (D194): the export instead, never required.
            deleteSheet.onClose();
            exportSheet.onOpen();
          }}
        />
      )}

      <FamilyNameHandleDialog
        isOpen={nameHandleDialog.isOpen}
        onClose={nameHandleDialog.onClose}
        family={family}
        canChangeHandle={isRoot}
        rename={(name) => updateFamily(family.id, { name })}
        onSaved={() => Promise.all([refresh(), loadFamilies()])}
      />

      <InvitePeopleSheet
        isOpen={familyLink.isOpen}
        onClose={familyLink.onClose}
        familyId={family.id}
        familyName={family.name}
        canManage={canApprove}
      />

      {/* QR / link dialog */}
      <Modal isOpen={qrDialog.isOpen} onClose={qrDialog.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('account.admin.inviteLink')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {created && (
                <TwindenQr
                  value={created.joinUrl}
                  label={t('account.admin.qrAlt')}
                  size="240px"
                />
              )}
              <Text fontSize="sm" color="muted" textAlign="center">
                {t('account.admin.qrEnroll', {
                  name: created?.token.displayName ?? '',
                  date: created
                    ? new Date(created.token.expiresAt).toLocaleString(
                        appLocale()
                      )
                    : '',
                })}
              </Text>
              <Code
                p={2}
                borderRadius="md"
                fontSize="xs"
                wordBreak="break-all"
                maxW="full"
              >
                {created?.joinUrl}
              </Code>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              leftIcon={<Copy size={16} />}
              onClick={() => created && copyLink(created.joinUrl)}
            >
              {t('account.profile.copyLink')}
            </Button>
            <Button variant="ghost" ml={3} onClick={qrDialog.onClose}>
              {t('common.done')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageScaffold>
  );
}
